/**
 * First-party search telemetry for the SSR path: every search the browser
 * engine executes is beaconed to `/api/coveo/pubsub` (Pub/Sub → BigQuery) with
 * the same body the CSR `interceptSearchMiddleware` sends.
 *
 * Mechanism: a Redux middleware, passed through `defineSearchEngine`'s
 * `middlewares` option. The SSR definer spreads its options into
 * `buildSearchEngine` (appending its own wait-for-search middleware after
 * ours), so this observes exactly the actions the CSR middleware does —
 * rejections and did-you-mean corrections included. The alternatives are
 * weaker: `preprocessSearchResponseMiddleware` only sees successful responses
 * (no errors, no corrections, no executed query), and `engine.subscribe` sees
 * state, not the actions that tell a fresh search from a hydration replay.
 *
 * Only searches executed in this browser are beaconed:
 * - On the server the middleware is inert. `fetchStaticState` runs per page
 *   view, crawlers included, so beaconing it would change what a "search
 *   execution" means in BigQuery.
 * - `hydrateStaticState` replays the server's `fulfilled` action with no
 *   `pending` before it, so a completion is only reported when this store saw
 *   its request start.
 *
 * Session key: the CSR path hashes the per-user search token, but the SSR path
 * shares one anonymous token across users, so hashing it would collapse every
 * session into one key. The per-browser Coveo client id is hashed instead —
 * the one the header search engine persists, with functional consent, as its
 * navigator-context client id. It is read, never written here, and only while
 * functional consent holds at the time of the search: consent withdrawn
 * mid-visit leaves the id in storage until the header engine next builds. A
 * browser without one, or without consent, falls back to a random id held in
 * memory for the tab's lifetime. The first consented search fixes the key for
 * the page load: on a first visit the header writes its id only once its token
 * fetch resolves, and switching to it mid-load would split one visit's beacons
 * across two keys. The body keeps the `hashedSearchToken` field name so the
 * Pub/Sub consumer schema is unchanged.
 */
import type { Middleware } from '@coveo/headless-react/ssr';
import { CoveoSearchHub } from '@lib/coveo/coveoConstants';
import { resolveSearchHub } from '@lib/coveo/interceptSearchMiddleware';
import * as Sentry from '@sentry/nextjs';
import { CLIENT_ID_STORAGE_KEY, hasFunctionalConsent } from './clientIdStorage';

export const SEARCH_TELEMETRY_ENDPOINT = '/api/coveo/pubsub';

const UNHASHABLE_SESSION_KEY = 'unableToHashString';
const DID_YOU_MEAN_CORRECTION = 'didYouMean/correction';

const STARTED_ACTIONS = new Set([
  'search/executeSearch/pending',
  'search/fetchPage/pending',
]);

const COMPLETED_ACTIONS = new Set([
  'search/executeSearch/fulfilled',
  'search/executeSearch/rejected',
  'search/fetchPage/fulfilled',
  'search/fetchPage/rejected',
]);

export type SearchTelemetryAction = {
  type: string;
  payload?: unknown;
  error?: { message?: string };
  meta?: { requestId?: string };
};

export type SearchTelemetryBody = {
  hashedSearchToken: string;
  advancedExpression: string;
  query: string;
  queryPipeline: string;
  url: string;
  isError: boolean;
};

type SearchCompletionPayload = {
  queryExecuted?: string;
  response?: { advancedExpression?: string; pipeline?: string };
};

type SearchTelemetryContext = {
  hashedSessionKey: string;
  url: string;
  searchHub: CoveoSearchHub;
};

export function buildSearchTelemetryBody(
  action: SearchTelemetryAction,
  { hashedSessionKey, url, searchHub }: SearchTelemetryContext
): SearchTelemetryBody {
  if (action.type === DID_YOU_MEAN_CORRECTION) {
    return {
      hashedSearchToken: hashedSessionKey,
      advancedExpression: 'didYouMeanCorrection',
      query: typeof action.payload === 'string' ? action.payload : '',
      queryPipeline: searchHub,
      url,
      isError: false,
    };
  }

  const payload = (action.payload ?? {}) as SearchCompletionPayload;
  return {
    hashedSearchToken: hashedSessionKey,
    advancedExpression: payload.response?.advancedExpression ?? '',
    query: payload.queryExecuted ?? '',
    queryPipeline: payload.response?.pipeline ?? '',
    url,
    isError: action.type.endsWith('/rejected'),
  };
}

const isAbortedSearch = (action: SearchTelemetryAction) =>
  action.type.endsWith('/rejected') &&
  (action.error?.message?.includes('signal is aborted without reason') ??
    false);

function readStoredClientId(): string | undefined {
  try {
    return window.localStorage.getItem(CLIENT_ID_STORAGE_KEY) || undefined;
  } catch {
    return undefined;
  }
}

let tabSessionId: string | undefined;

function getTabSessionId(): string {
  tabSessionId ??=
    typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return tabSessionId;
}

let consentedSessionKey: string | undefined;

export function getSessionKey(): string {
  if (!hasFunctionalConsent()) {
    return getTabSessionId();
  }
  consentedSessionKey ??= readStoredClientId() ?? getTabSessionId();
  return consentedSessionKey;
}

async function sha256Hex(input: string): Promise<string> {
  try {
    const digest = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(input)
    );
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  } catch (error) {
    Sentry.captureException(error);
    return UNHASHABLE_SESSION_KEY;
  }
}

const hashedKeys = new Map<string, Promise<string>>();

function hashSessionKey(sessionKey: string): Promise<string> {
  let hashed = hashedKeys.get(sessionKey);
  if (!hashed) {
    hashed = sha256Hex(sessionKey);
    hashedKeys.set(sessionKey, hashed);
  }
  return hashed;
}

export function sendSearchTelemetry(body: SearchTelemetryBody) {
  const serialized = JSON.stringify(body);
  try {
    if (
      typeof navigator.sendBeacon === 'function' &&
      navigator.sendBeacon(SEARCH_TELEMETRY_ENDPOINT, serialized)
    ) {
      return;
    }
  } catch {
    // sendBeacon can throw (e.g. blocked by an extension); fall back to fetch.
  }
  fetch(SEARCH_TELEMETRY_ENDPOINT, {
    method: 'POST',
    body: serialized,
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
  }).catch(() => undefined);
}

const isTelemetryAction = (action: unknown): action is SearchTelemetryAction =>
  typeof action === 'object' &&
  action !== null &&
  typeof (action as { type?: unknown }).type === 'string';

export const searchTelemetryMiddleware: Middleware<
  object,
  { searchHub?: string }
> = (api) => {
  if (typeof window === 'undefined') {
    return (next) => next;
  }

  const startedRequestIds = new Set<string>();

  const shouldReport = (action: SearchTelemetryAction) => {
    if (action.type === DID_YOU_MEAN_CORRECTION) {
      return true;
    }
    if (!COMPLETED_ACTIONS.has(action.type)) {
      return false;
    }
    const requestId = action.meta?.requestId;
    const wasStartedHere =
      requestId !== undefined && startedRequestIds.delete(requestId);
    return wasStartedHere && !isAbortedSearch(action);
  };

  return (next) => (action) => {
    if (!isTelemetryAction(action)) {
      return next(action);
    }
    if (STARTED_ACTIONS.has(action.type) && action.meta?.requestId) {
      startedRequestIds.add(action.meta.requestId);
    } else if (shouldReport(action)) {
      const searchHub = resolveSearchHub(api.getState().searchHub);
      hashSessionKey(getSessionKey()).then((hashedSessionKey) =>
        sendSearchTelemetry(
          buildSearchTelemetryBody(action, {
            hashedSessionKey,
            url: window.location.href,
            searchHub,
          })
        )
      );
    }
    return next(action);
  };
};
